import {
	queryCapacityAction,
	returnCapacityAction,
	useCapacityAction,
} from '../actions/capacity.actions';
import {
	QueryCapacityRequestOptions,
	QueryCapacityResponseBody,
	ReturnCapacityRequestOptions,
	UseCapacityRequestOptions,
} from '../services/capacity';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { CapacityStoreState } from '../reducers/capacity.reducer';
import { IDLE_QUERY_CAPACITY, CapacityItem } from '../utils/capacity';
import { selectCapacityStore } from '../selectors/capacity.selectors';
import { useCapacityFeatureFlags } from './useCapacityFeatureFlags';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';

// This is a valid use case of any.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InferPayload<T extends ActionCreatorWithPayload<any>> =
	T extends ActionCreatorWithPayload<infer P> ? P : never;

// This is a valid use case of any.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InferResponseData<T extends ActionCreatorWithPayload<any>> =
	InferPayload<T> extends { resolve: (data: infer D) => void } ? D : never;

type InferHasAvailableCapacityArgs<T> = T extends {
	// This is a valid use case of any.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(a: infer A, d: infer D): any;
	// This is a valid use case of any.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(b: infer B): any;
	// This is a valid use case of any.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(c: infer C): any;
}
	? [A | B | C, D]
	: never;

export type CapacityUtils = {
	/** A flag that controls wether or not the capacity feature is enabled. */
	isCapacityFeatureEnabled: boolean;
	/**
	 * Triggers an API call to the capacity API to put some `quantity` in use for a specific `item`.
	 *
	 * When you call this function, the capacity stored in redux will be updated as well and values returned from
	 * currentCapacity(item) will contain "the most recent snapshot" of the item's capacity.
	 *
	 * @param item The capacity item you want to use capacity.
	 * @param quantity The amount of capacity to put in use.
	 * @returns The item's capacity after the operation.
	 */
	putCapacityInUse(
		item: UseCapacityRequestOptions['item'],
		quantity: number,
	): Promise<QueryCapacityResponseBody>;
	/**
	 * Returns the current capacity stored in redux for a given `item`.
	 *
	 * This function does not trigger an API call.
	 * If you need an up-to-date value from the server, use queryCapacity(item) instead.
	 *
	 * The main reason to use this function is to render the current capacity in UI elements where "real-time" values are
	 * not necessary, like the sensors count on admin's sensor configuration page.
	 *
	 * Whenever new capacity is requested to the user, the values returned by this function will be updated to show you
	 * "the most recent snapshot" of the capacity.
	 *
	 * @param item The capacity item you want to show.
	 * @returns The item's capacity.
	 */
	currentCapacity(
		item: CapacityItem,
	): CapacityStoreState[keyof CapacityStoreState];
	/**
	 * Triggers an API call to the capacity API to get the most recent capacity of a given `item`.
	 *
	 * This function can be awaited, if it succeeds the returned value will give you the response of the API.
	 * If it fails, the function will throw a capacity request error.
	 *
	 * When you call this function, the capacity stored in redux will be updated as well and values returned from
	 * currentCapacity(item) will contain "the most recent snapshot" of the item's capacity.
	 *
	 * @param item The capacity item you want to show.
	 * @returns The item's capacity.
	 */
	queryCapacity(
		item: QueryCapacityRequestOptions['item'],
	): Promise<QueryCapacityResponseBody>;
	/**
	 * Triggers an API call to the capacity API to return some `quantity` currently in use for a specific `item`.
	 *
	 * When you call this function, the capacity stored in redux will be updated as well and values returned from
	 * currentCapacity(item) will contain "the most recent snapshot" of the item's capacity.
	 *
	 * @param item The capacity item you want to return capacity.
	 * @param quantity The amount of capacity to put in use.
	 * @returns The item's capacity after the operation.
	 */
	returnUsedCapacity(
		item: ReturnCapacityRequestOptions['item'],
		quantity: number,
	): Promise<QueryCapacityResponseBody>;
	/** */
	hasAvailableCapacity: {
		/**
		 * Checks if there is available capacity for a given item based on the latest information cached on redux.
		 *
		 * @param item The item whose capacity needs to be checked.
		 * @param defaultValue A fallback value to be used when capacity can't be determined.
		 */
		(item: CapacityItem, defaultValue?: boolean): boolean;
		/**
		 * Checks if there is available capacity based on a previous capacity response.
		 *
		 * @param response A response object containing capacity information.
		 */
		(response: QueryCapacityResponseBody): boolean;
		/**
		 * Checks if there is available capacity based based on a value.
		 *
		 * @param available A value representing the available capacity.
		 */
		(available: number | null | undefined): boolean;
	};
};

/** A set of utilities to check capacity. */
export function useCapacity(): CapacityUtils {
	const dispatch = useDispatch();
	const store = useSelector(selectCapacityStore);
	const { isCapacityFeatureEnabled } = useCapacityFeatureFlags();

	const dispatchAction = React.useCallback(
		// This is a valid use case of any.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		<T extends ActionCreatorWithPayload<any>>(
			action: T,
			payload: Omit<InferPayload<T>, 'resolve' | 'reject'>,
		): Promise<InferResponseData<T>> => {
			return new Promise((resolve, reject) => {
				dispatch(action({ ...payload, resolve, reject }));
			});
		},
		[dispatch],
	);

	const currentCapacity: CapacityUtils['currentCapacity'] = React.useCallback(
		item => {
			if (!isCapacityFeatureEnabled) {
				return {
					metadata: {
						initialized: true,
						ready: true,
						stale: false,
						status: 'success',
					},
					data: { inUse: -9999, unlimited: true },
					error: null,
				};
			}

			const i = item.toLowerCase();
			if (typeof store[i] === 'undefined') return IDLE_QUERY_CAPACITY;
			return store[i];
		},
		[isCapacityFeatureEnabled, store],
	);

	const queryCapacity: CapacityUtils['queryCapacity'] = React.useCallback(
		async item => {
			if (!isCapacityFeatureEnabled) {
				return { unlimited: true, inUse: -9999 };
			}

			return await dispatchAction(queryCapacityAction, { item });
		},
		[isCapacityFeatureEnabled, dispatchAction],
	);

	const putCapacityInUse: CapacityUtils['putCapacityInUse'] =
		React.useCallback(
			async (item, quantity) => {
				if (!isCapacityFeatureEnabled) {
					return { unlimited: true, inUse: -9999 };
				}

				return await dispatchAction(useCapacityAction, {
					item,
					quantity,
				});
			},
			[isCapacityFeatureEnabled, dispatchAction],
		);

	const returnUsedCapacity: CapacityUtils['returnUsedCapacity'] =
		React.useCallback(
			async (item, quantity) => {
				if (!isCapacityFeatureEnabled) {
					return { unlimited: true, inUse: -9999 };
				}

				return await dispatchAction(returnCapacityAction, {
					item,
					quantity,
				});
			},
			[isCapacityFeatureEnabled, dispatchAction],
		);

	const hasAvailableCapacity: CapacityUtils['hasAvailableCapacity'] =
		React.useCallback(
			((
				...args: InferHasAvailableCapacityArgs<
					CapacityUtils['hasAvailableCapacity']
				>
			) => {
				if (!isCapacityFeatureEnabled) return true;

				const a = args[0];
				const b = args[1];
				let available: number;

				if (typeof a === 'string') {
					const capacity = currentCapacity(a);
					const { data, error, metadata } = capacity;
					const { initialized } = metadata;
					const defaultValue = b ?? false;

					if (!initialized || !data || error) return defaultValue;
					if (data.unlimited) return true;

					available = data.available;
				} else if (typeof a === 'number') {
					available = a;
				} else if (typeof a === 'undefined' || a === null) {
					return false;
				} else {
					if (a.unlimited) return true;
					available = a.available;
				}

				return !!Math.max(0, available);
			}) as CapacityUtils['hasAvailableCapacity'],
			[isCapacityFeatureEnabled, currentCapacity],
		);

	return {
		isCapacityFeatureEnabled,
		currentCapacity,
		queryCapacity,
		putCapacityInUse,
		returnUsedCapacity,
		hasAvailableCapacity,
	};
}
